// Shapes of GET/PUT {masteruri}/AuthSettings and the connection tests.
// All of it is Admin only on the hub.

/** The login methods the hub knows. Order in the list = order of checking. */
export const AUTH_PROVIDERS = ["local", "activedirectory", "ldap", "keycloak"] as const;
export type AuthProvider = typeof AUTH_PROVIDERS[number];

/**
 * Roles an automatically provisioned account may get. The machine roles
 * (Robot, Scanner, SoftwareClient, Enroller) are left out on purpose: a person
 * logging in through the directory must never become one.
 */
export const PROVISION_ROLES = ["Guest", "User", "Admin"] as const;
export type ProvisionRole = typeof PROVISION_ROLES[number];

export interface LdapSettings {
    readonly server: string;
    readonly port: number | null;
    readonly baseDn: string;
    readonly bindDn: string;
    readonly bindPasswordSet: boolean;
    readonly userAttribute: string;
    readonly group: string;
    readonly autoProvision: boolean;
}

export interface KeycloakSettings {
    readonly url: string;
    readonly realm: string;
    readonly clientId: string;
    readonly clientSecretSet: boolean;
    readonly role: string;
    readonly autoProvision: boolean;
    readonly redirectUri: string;
    readonly scope: string;
}

/** What the page edits. `providers` is the parsed form of `authProvider`. */
export interface AuthSettings {
    readonly providers: readonly AuthProvider[];
    readonly provisionRole: ProvisionRole;
    readonly ldap: LdapSettings;
    readonly keycloak: KeycloakSettings;
}

/**
 * Write-only secrets. Empty means "keep what the hub has stored"; they are
 * never read back from the hub, only replaced.
 */
export interface AuthSecrets {
    readonly bindPassword: string;
    readonly clientSecret: string;
}

export type ConnectionKind = "ldap" | "keycloak";

export type TestResult =
    | { readonly kind: "idle" }
    | { readonly kind: "running" }
    | { readonly kind: "done"; readonly ok: boolean; readonly message: string };

export type LdapField = keyof Omit<LdapSettings, "bindPasswordSet">;
export type KeycloakField = keyof Omit<KeycloakSettings, "clientSecretSet">;
